/**
 * Product Preview Carousel Click Fix
 * Ensures direct navigation on product title/image clicks without interference.
 * Also handles cross-browser scroll indicator (required for iOS Safari which
 * does not support ::-webkit-scrollbar pseudo-elements).
 */

(function () {
  if (window.__ppCarouselFixLoaded) {
    return;
  }
  window.__ppCarouselFixLoaded = true;

  document.addEventListener('DOMContentLoaded', function () {
    const carouselSections = document.querySelectorAll(
      '.product-preview-carousel'
    );

    if (!carouselSections.length) return;

    carouselSections.forEach(function (section) {
      initClickFix(section);
      initScrollIndicator(section);
    });
  });

  /**
   * Adds cursor: grab / cursor: grabbing state to the images row.
   * Gated by CSS @media (hover: hover) — no visual effect on touch-only devices.
   * document-level pointerup ensures the class is removed even if pointer leaves
   * the container while dragging. Cleans up on shopify:section:unload.
   */
  function initDragCursor(imagesRow, sectionId) {
    var removeDragging = function () {
      imagesRow.classList.remove('is-dragging');
    };
    imagesRow.addEventListener('pointerdown', function () {
      imagesRow.classList.add('is-dragging');
    });
    document.addEventListener('pointerup', removeDragging);
    document.addEventListener('pointercancel', removeDragging);

    if (sectionId) {
      document.addEventListener(
        'shopify:section:unload',
        function cleanupDragListeners(e) {
          if (e.detail.sectionId === sectionId) {
            document.removeEventListener('pointerup', removeDragging);
            document.removeEventListener('pointercancel', removeDragging);
            document.removeEventListener(
              'shopify:section:unload',
              cleanupDragListeners
            );
          }
        }
      );
    }
  }

  function initClickFix(section) {
    const productLinks = section.querySelectorAll(
      '.product-preview-section-image-block a'
    );

    productLinks.forEach(function (link) {
      // NOTE: Do NOT use cloneNode + replaceChild here — cloning severs the
      // original DOM element identity that analytics tools (FigPii, Hotjar)
      // rely on. Clicks on cloned nodes appear as "blank/empty target" in
      // heatmaps. A capturing listener on the original element achieves the
      // same click-fix behavior without breaking element tracking.
      link.addEventListener(
        'click',
        function (e) {
          const href = this.getAttribute('href');
          if (href && href !== '#' && href !== '') {
            e.stopImmediatePropagation();
            window.location.href = href;
          }
        },
        true
      );
    });
  }

  /**
   * Detects perceived luminance of the section background color and applies
   * dark scrollbar/indicator colors when the background is light (e.g. white).
   * Sets CSS custom properties on imagesRow (for ::-webkit-scrollbar) and on
   * indicatorWrapper/indicatorThumb (for the JS indicator).
   */
  function applyScrollbarTheme(imagesRow, indicatorWrapper, indicatorThumb) {
    const rawColor = getComputedStyle(imagesRow)
      .getPropertyValue('--scrollbar-track')
      .trim();
    if (!rawColor) return;

    var r = 0,
      g = 0,
      b = 0;
    var hex6 = rawColor.match(/^#([0-9a-f]{6})$/i);
    var hex3 = rawColor.match(/^#([0-9a-f]{3})$/i);
    var rgbMatch = rawColor.match(/^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/i);

    if (hex6) {
      r = parseInt(hex6[1].slice(0, 2), 16);
      g = parseInt(hex6[1].slice(2, 4), 16);
      b = parseInt(hex6[1].slice(4, 6), 16);
    } else if (hex3) {
      r = parseInt(hex3[1][0] + hex3[1][0], 16);
      g = parseInt(hex3[1][1] + hex3[1][1], 16);
      b = parseInt(hex3[1][2] + hex3[1][2], 16);
    } else if (rgbMatch) {
      r = parseInt(rgbMatch[1]);
      g = parseInt(rgbMatch[2]);
      b = parseInt(rgbMatch[3]);
    } else {
      return;
    }

    // Perceived luminance: 0 = black, 1 = white
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    if (luminance > 0.5) {
      // Light background — swap to dark scrollbar colors
      imagesRow.style.setProperty('--scrollbar-thumb', 'rgba(0, 0, 0, 0.35)');
      imagesRow.style.setProperty(
        '--scrollbar-thumb-hover',
        'rgba(0, 0, 0, 0.6)'
      );
      imagesRow.style.setProperty(
        '--scrollbar-track-border',
        'rgba(0, 0, 0, 0.15)'
      );
      indicatorWrapper.style.setProperty(
        '--scrollbar-indicator-bg',
        'rgba(0, 0, 0, 0.1)'
      );
      indicatorThumb.style.setProperty(
        '--scrollbar-indicator-thumb',
        'rgba(0, 0, 0, 0.35)'
      );
    }
  }

  /**
   * Custom scroll indicator driven by JS.
   * Only shown on browsers where ::-webkit-scrollbar CSS is unsupported (iOS Safari).
   * On Chrome/Safari desktop/Android, the CSS scrollbar takes layout space
   * (offsetHeight > clientHeight), so the JS indicator stays hidden.
   */
  function initScrollIndicator(section) {
    const imagesRow = section.querySelector(
      '.product-preview-section-images-row'
    );
    const indicatorWrapper = section.querySelector(
      '.product-preview-scroll-indicator-wrapper'
    );
    const indicatorThumb = section.querySelector(
      '.product-preview-scroll-indicator-thumb'
    );

    if (!imagesRow || !indicatorWrapper || !indicatorThumb) return;

    const shopifySection = section.closest('[id^="shopify-section-"]');
    const sectionId = shopifySection
      ? shopifySection.id.replace('shopify-section-', '')
      : null;
    initDragCursor(imagesRow, sectionId);
    applyScrollbarTheme(imagesRow, indicatorWrapper, indicatorThumb);

    // Whether the browser is rendering a CSS ::-webkit-scrollbar (which takes layout space).
    // iOS Safari never does, so offsetHeight === clientHeight there.
    // This only changes on resize/orientation change — not on scroll — so we cache it.
    let hasCSSScrollbar = false;

    function checkScrollbarType() {
      hasCSSScrollbar = imagesRow.offsetHeight > imagesRow.clientHeight;
      if (hasCSSScrollbar) indicatorWrapper.style.display = 'none';
    }

    function updateIndicator() {
      // If the CSS scrollbar is rendering, nothing to do.
      if (hasCSSScrollbar) return;

      // Batch all layout reads first to avoid forced reflow from interleaved read/write
      const scrollWidth = imagesRow.scrollWidth;
      const clientWidth = imagesRow.clientWidth;
      const scrollLeft = imagesRow.scrollLeft;

      // Write phase — no layout reads after this point
      if (scrollWidth <= clientWidth) {
        indicatorWrapper.style.display = 'none';
        return;
      }

      indicatorWrapper.style.display = 'block';

      const thumbWidthPct = (clientWidth / scrollWidth) * 100;
      const maxScrollLeft = scrollWidth - clientWidth;
      const thumbLeftPct =
        maxScrollLeft > 0
          ? (scrollLeft / maxScrollLeft) * (100 - thumbWidthPct)
          : 0;

      indicatorThumb.style.width = thumbWidthPct + '%';
      indicatorThumb.style.left = thumbLeftPct + '%';
    }

    imagesRow.addEventListener('scroll', updateIndicator, { passive: true });

    // ResizeObserver is more precise than window resize — fires when the row's
    // own dimensions change (font scaling, orientation, sidebar open/close, etc.)
    // and avoids the forced layout reads that window resize triggers globally.
    const resizeObserver = new ResizeObserver(function () {
      checkScrollbarType();
      if (!hasCSSScrollbar) requestAnimationFrame(updateIndicator);
    });
    resizeObserver.observe(imagesRow);

    // Run once after layout so initial overflow and scrollbar type are detected
    requestAnimationFrame(function () {
      checkScrollbarType();
      updateIndicator();
    });
  }
})();
